import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Crown, MapPin, ArrowRight, Sparkles, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TV_FRAME_URL =
  "https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/dtloe834_ChatGPT%20Image%20Jun%205%2C%202026%2C%2005_03_10%20PM.png";

function initialsOf(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

export default function OnlyBestCreatorOfMonthSpotlight() {
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/onlybest/creator-of-the-month`);
        if (alive) setCreator(r.data?.creator || null);
      } catch {
        // silent fail — spotlight just hides
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !creator) return null;

  const location = [creator.city, creator.state].filter(Boolean).join(", ");
  const listingPath = `/business/${creator.slug}`;
  const hasVideo = Boolean(creator.video_url);

  return (
    <section
      className="relative w-full overflow-hidden border-t border-white/5"
      data-testid="creator-of-the-month-section"
    >
      {/* Pill badge above the TV */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F59E0B]/15 to-[#EC4899]/15 border border-[#F59E0B]/30"
        >
          <Crown className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#F59E0B]">
            OnlyBest Creator of the Month
          </span>
        </motion.div>
      </div>

      {/* TV frame — full-bleed, mobile scaled */}
      <div className="relative w-full overflow-hidden" data-testid="cotm-tv-wrapper">
        <div className="relative md:w-full md:mx-0 w-[171%] mx-[-35.5%]">
          <img
            src={TV_FRAME_URL}
            alt={`${creator.name} cinematic spotlight`}
            className="block w-full h-auto select-none pointer-events-none"
            draggable="false"
          />
          <div
            className="absolute overflow-hidden bg-black flex items-center justify-center"
            style={{ top: "15%", left: "21%", width: "58.5%", height: "57.5%" }}
            data-testid="cotm-screen"
          >
            {hasVideo ? (
              <video
                src={creator.video_url}
                autoPlay
                loop
                muted
                playsInline
                controls
                className="w-full h-full object-cover"
                data-testid="cotm-video"
              />
            ) : (
              <CinematicPlaceholder creator={creator} />
            )}
          </div>
        </div>
      </div>

      {/* Info + CTA — side by side under the TV */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-12 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex-1 text-center md:text-left"
          >
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white tracking-tight"
              data-testid="cotm-name"
            >
              {creator.name}
            </h2>
            <p className="text-[#94A3B8] text-sm sm:text-base mt-2 flex items-center justify-center md:justify-start gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Flame className="w-4 h-4 text-[#F59E0B]" />
                {creator.creator_type}
              </span>
              {location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#00E5FF]" />
                  {location}
                </span>
              )}
            </p>
            {(creator.post_count > 0 || creator.lead_count > 0) && (
              <p className="mt-3 text-xs text-[#64748B] flex items-center justify-center md:justify-start gap-3 flex-wrap">
                <span>
                  <span className="text-white font-semibold">
                    {creator.post_count}
                  </span>{" "}
                  posts
                </span>
                <span>
                  <span className="text-white font-semibold">
                    {creator.lead_count}
                  </span>{" "}
                  leads
                </span>
                {creator.is_best_of_best && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#F59E0B]">
                    <Crown className="w-3 h-3" /> Best of Best
                  </span>
                )}
              </p>
            )}
          </motion.div>

          <div className="flex items-center justify-center md:justify-end gap-4 flex-shrink-0">
            {creator.logo_url ? (
              <img
                src={creator.logo_url}
                alt={`${creator.name} logo`}
                className="w-16 h-16 rounded-xl object-contain bg-white/5 border border-white/10 p-1"
                data-testid="cotm-logo"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center text-white font-bold text-lg border border-white/10">
                {initialsOf(creator.name) || "OB"}
              </div>
            )}
            <Link
              to={listingPath}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/20 transition-all"
              data-testid="cotm-cta-visit"
            >
              Visit {creator.name.split(" ")[0]}&apos;s Profile
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CinematicPlaceholder({ creator }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 bg-gradient-to-br from-[#0F172A] via-[#0B1226] to-[#1F0F2E]">
      <Sparkles className="w-10 h-10 text-[#F59E0B] mb-3 animate-pulse" />
      <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#F59E0B] mb-2">
        Creator of the Month
      </p>
      <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] tracking-tight leading-tight">
        {creator.name}
      </h3>
      <p className="mt-3 text-xs sm:text-sm text-[#94A3B8] max-w-md">
        Cinematic Video coming soon from {creator.name.split(" ")[0]} — stay tuned.
      </p>
    </div>
  );
}
